/**
 * Game Context - State Management for Interactive Game
 * Follows UXProvider pattern with Context + localStorage
 */

import { useState, useEffect, useRef, ReactNode } from 'react'
import type { InteractiveGameState, SkillCategory } from '../types'
import { GameEngine, createInitialState } from '../engine/game-engine'
import { GameContext, type GameContextType } from './GameContextDef'

const STORAGE_KEY = 'portfolio-game-state'
const SAVE_VERSION = 1
const GAME_FPS = 60
const TICK_INTERVAL = 1000 / GAME_FPS // 16ms for 60 FPS
const AUTO_SAVE_INTERVAL = 5000 // Save every 5 seconds

interface SaveData {
  version: number
  state: {
    resources: InteractiveGameState['resources']
    resourceCaps: InteractiveGameState['resourceCaps']
    unlockedSkills: string[]
    skillLevels: Record<string, number>
    multipliers: InteractiveGameState['multipliers']
    activeSynergies: string[]
    totalClicks: number
    playtime: number
    lastSave: number
    lastTick: number
  }
}

/**
 * Load game state from localStorage
 */
function loadGameState(): InteractiveGameState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null

    const data: SaveData = JSON.parse(stored)

    // Check version
    if (data.version !== SAVE_VERSION) {
      console.warn('Game save version mismatch, resetting')
      return null
    }

    // Reconstruct Sets (JSON doesn't preserve them)
    const state: InteractiveGameState = {
      ...data.state,
      unlockedSkills: new Set(data.state.unlockedSkills),
      activeSynergies: new Set(data.state.activeSynergies),
      // Ensure resourceCaps exists (for old saves)
      resourceCaps: data.state.resourceCaps || {
        infrastructure: 100,
        automation: 100,
        data: 100,
        operations: 100,
      },
    }

    return state
  } catch (error) {
    console.warn('Failed to load game state:', error)
    return null
  }
}

/**
 * Save game state to localStorage
 */
function saveGameState(state: InteractiveGameState): void {
  try {
    const saveData: SaveData = {
      version: SAVE_VERSION,
      state: {
        ...state,
        unlockedSkills: Array.from(state.unlockedSkills),
        activeSynergies: Array.from(state.activeSynergies),
      },
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(saveData))
  } catch (error) {
    console.warn('Failed to save game state:', error)
  }
}

export function GameProvider({ children }: { children: ReactNode }) {
  // Initialize state and check if we have a saved game
  const [state, setState] = useState<InteractiveGameState>(() => {
    const loaded = loadGameState()
    return loaded || createInitialState()
  })

  // Set isGameActive based on whether we loaded a saved game
  const [isGameActive, setIsGameActive] = useState(() => {
    const loaded = loadGameState()
    return loaded !== null
  })

  const engineRef = useRef(new GameEngine(state))

  // Update engine state when state changes
  useEffect(() => {
    engineRef.current = new GameEngine(state)
  }, [state])

  // Game loop (60 FPS)
  useEffect(() => {
    if (!isGameActive) return

    const interval = setInterval(() => {
      const deltaTime = TICK_INTERVAL / 1000 // Convert to seconds
      const newState = engineRef.current.tick(deltaTime)
      setState(newState)
    }, TICK_INTERVAL)

    return () => clearInterval(interval)
  }, [isGameActive])

  // Auto-save every 5 seconds
  useEffect(() => {
    if (!isGameActive) return

    const saveInterval = setInterval(() => {
      saveGameState(state)
    }, AUTO_SAVE_INTERVAL)

    return () => clearInterval(saveInterval)
  }, [state, isGameActive])

  // Calculate offline progress on mount
  useEffect(() => {
    if (!isGameActive) return

    const loaded = loadGameState()
    if (loaded && loaded.lastSave) {
      const offlineIncome = engineRef.current.calculateOfflineProgress(loaded.lastSave)
      const hasOfflineProgress =
        offlineIncome.infrastructure > 0 ||
        offlineIncome.automation > 0 ||
        offlineIncome.data > 0 ||
        offlineIncome.operations > 0

      if (hasOfflineProgress) {
        engineRef.current.applyOfflineProgress(offlineIncome)
        const updatedState = engineRef.current.getState()
        setState(updatedState)

        // TODO: Show notification about offline progress
        console.log('Offline progress applied:', offlineIncome)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  const click = (resource: SkillCategory) => {
    if (!isGameActive) return
    const newState = engineRef.current.click(resource)
    setState(newState)
  }

  const unlockSkill = (skillId: string) => {
    if (!isGameActive) return { success: false, message: 'Game not started' }
    const result = engineRef.current.unlockSkill(skillId)
    if (result.success) {
      const newState = engineRef.current.getState()
      setState(newState)
    }
    return result
  }

  const canAfford = (skillId: string): boolean => {
    return engineRef.current.canAfford(skillId)
  }

  const hasPrerequisites = (skillId: string): boolean => {
    return engineRef.current.hasPrerequisites(skillId)
  }

  const getPassiveIncome = (): InteractiveGameState['resources'] => {
    return engineRef.current.calculatePassiveIncome()
  }

  const resetGame = () => {
    if (confirm('Reset game? This will delete all progress.')) {
      localStorage.removeItem(STORAGE_KEY)
      const newState = createInitialState()
      setState(newState)
      setIsGameActive(false)
    }
  }

  const saveGame = () => {
    saveGameState(state)
  }

  const startGame = () => {
    setIsGameActive(true)
    // Initialize state if needed
    if (state.resources.operations === 0) {
      const newState = createInitialState()
      setState(newState)
    }
  }

  const contextValue: GameContextType = {
    state,
    click,
    unlockSkill,
    canAfford,
    hasPrerequisites,
    getPassiveIncome,
    resetGame,
    saveGame,
    isGameActive,
    startGame,
  }

  return <GameContext.Provider value={contextValue}>{children}</GameContext.Provider>
}
